import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation, validationToastMessage } from '../useApiMutation';
import { apiPost, operationsClient } from '@/lib/api';
import { opsEstimateEndpoints } from '@/lib/api/endpoints';
import { estimateKeys, jobKeys } from '@/lib/queryKeys';
import type { Estimate, EstimateFulfillmentMode } from '@/types/estimate';

/** The methods an office user can witness; the server refuses the others here. */
export type OfficeApprovalMethod = 'VERBAL' | 'EMAIL';

/**
 * Approves an estimate via the operations microservice.
 *
 * The one refusal this used to own its toast for — NOT-584's missing job type,
 * which needed a "Set job type" action because the field lives on the estimate
 * form rather than the approve screen — no longer exists: the job type is
 * chosen at conversion, so approval does not ask for it (owner's ruling
 * 2026-08-17). Errors take the standard toast again, field errors included.
 */
export function useApproveEstimate() {
  const queryClient = useQueryClient();

  return useApiMutation<
    Estimate,
    {
      id: string;
      fulfillmentMode?: EstimateFulfillmentMode;
      /** How the customer actually approved — the office's word, VERBAL or EMAIL. */
      customerApprovalMethod?: OfficeApprovalMethod;
      /**
       * Who closed the sale — becomes the estimate's salesperson (NOT-562
       * closer credit). Omitted, the stored salesperson stands.
       */
      closedByUserId?: string;
    }
  >({
    mutationFn: ({ id, fulfillmentMode, customerApprovalMethod, closedByUserId }) =>
      apiPost<Estimate>(operationsClient, opsEstimateEndpoints.approve(id), {
        ...(fulfillmentMode ? { fulfillmentMode } : {}),
        ...(customerApprovalMethod ? { customerApprovalMethod } : {}),
        ...(closedByUserId ? { closedByUserId } : {}),
      }),
    showErrorToast: false,
    onError: (error) => {
      const fieldErrors = error.fieldErrors;
      if (fieldErrors && fieldErrors.length > 0) {
        toast.error(validationToastMessage(fieldErrors));
        return;
      }
      if (error.code === 'CANCELED') return;
      toast.error(error.message);
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: estimateKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: estimateKeys.detail(data.id) });
      void queryClient.invalidateQueries({ queryKey: jobKeys.all() });
      toast.success('Estimate approved');
    },
  });
}
